import { notification } from 'antd';

import axios, { type AxiosError, type Method } from 'axios';
import ApiError from 'errors/api';
import { apiResponseFormatter } from 'formatters/apiResponse';
import { BehaviorSubject, combineLatest, from, NEVER, of, type Observable, EMPTY } from 'rxjs';
import { catchError, first, logError, map, shareReplay, switchMap, tap } from 'rxjs-operators';
import { v4 } from 'uuid';

import { API_ENDPOINT, REACT_SECRET, REACT_TOKEN } from '../settings';
import tokenService, { type IAuth } from './token';
import userService from './user';

type RequestOptions = {
  retry?: boolean;
  supressError?: boolean;
  ignoreErrorHandler?: boolean;
  timeout?: number;
};

export class ApiService {
  private tokenRefreshRequest$: Observable<IAuth>;

  constructor(private apiEndpoint: string) {}

  public get<T = any>(url: string, params?: any, options?: RequestOptions): Observable<T> {
    return this.request('GET', url, params, options);
  }

  public post<T = any>(url: string, body: any, options?: RequestOptions): Observable<T> {
    return this.request('POST', url, body, options);
  }

  public put<T = any>(url: string, body: any): Observable<T> {
    return this.request('PUT', url, body);
  }

  public patch<T = any>(url: string, body: any): Observable<T> {
    return this.request('PATCH', url, body);
  }

  public delete<T = any>(url: string, params?: any): Observable<T> {
    return this.request('DELETE', url, params);
  }

  public getWithHeader<T = any>(url: string, params: any, headers: any, options?: RequestOptions): Observable<T> {
    return this.request('GET', url, params, options, false, headers);
  }

  public postWithHeader<T = any>(url: string, body: any, headers: any): Observable<T> {
    return this.request('POST', url, body, null, false, headers);
  }

  public putWithHeader<T = any>(url: string, body: any, headers: any): Observable<T> {
    return this.request('PUT', url, body, null, false, headers);
  }

  public handleErrorMessage(err: any) {
    if (err.message === 'Você não possui permissão para realizar esta ação') {
      notification.error({ message: err.message });
    } else {
      if (err.details !== undefined) {
        const errMsg = Array.isArray(err.details) ? err.details[0] : err.details;
        notification.error({ message: errMsg });
      } else {
        notification.error({ message: 'Ocorreu um erro. Tente novamente' });
      }
    }
  }

  private request<T>(
    method: Method,
    url: string,
    data: any = null,
    option: RequestOptions = null,
    ignoreBearerToken = false,
    customHeaders: any = null
  ): Observable<T> {
    option = option || {};
    option = {
      retry: false,
      supressError: false,
      ...option
    };
    const progress$ = new BehaviorSubject(0);

    return of(true).pipe(
      switchMap(() => (ignoreBearerToken ? of(null) : this.getBearerToken())),
      map(token => (token ? { Authorization: token } : null)),
      map(headers => {
        return {
          'token': REACT_TOKEN,
          'secret': REACT_SECRET,
          'X-Request-Uuid-Signature': v4(),
          'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',
          ...customHeaders,
          ...headers
        };
      }),
      switchMap(headers => {
        return axios.request({
          baseURL: this.apiEndpoint,
          url,
          method,
          timeout: option.timeout || 90000,
          headers: {
            'Content-type': 'application/json',
            ...headers
          },
          params: method === 'GET' ? data : null,
          data: method !== 'GET' ? data : null,
          onUploadProgress: (progress: ProgressEvent) => {
            const result = progress.loaded / progress.total;
            progress$.next(result * 100);
          }
        });
      }),
      tap(() => progress$.next(100)),
      map(res => {
        if (res?.data.code === 'ERR_DP409') {
          throw res;
        }

        return apiResponseFormatter(res.data) || undefined;
      }),
      catchError(err => {
        progress$.complete();
        return this.handleError(err, option);
      })
    );
  }

  private getBearerToken() {
    return combineLatest([tokenService.getTokens(), userService.getUser()]).pipe(
      first(),
      switchMap(([tokens, user]) => {
        if (!tokens || !user) return of(null);

        const now = (Date.now().valueOf() + 500) / 1000;

        if (!user.exp || user.exp > now) {
          return of(tokens.token);
        }

        if (!this.tokenRefreshRequest$) {
          this.tokenRefreshRequest$ = from(
            this.request('POST', '/auth_v2/refresh', { jwt_refresh_token: tokens.refresh }, {}, true)
          ).pipe(
            map(({ data }) => ({
              token: data.jwt_token,
              refresh: data.jwt_refresh_token
            })),
            switchMap((tokens: IAuth) => tokenService.setTokens(tokens)),
            tap(() => (this.tokenRefreshRequest$ = null)),
            logError(),
            catchError(() => {
              window.location.reload();
              return NEVER;
            }),
            shareReplay(1)
          );
        }

        return this.tokenRefreshRequest$.pipe(map(tokens => tokens?.token));
      }),
      map(accessToken => (accessToken ? `Bearer ${accessToken}` : null))
    );
  }

  private handleError(err: AxiosError, option: RequestOptions) {
    if (option.ignoreErrorHandler) {
      throw err;
    }

    if (!err.config) {
      return this.formatError(err, option);
    }

    if (err.response?.status === 401) {
      const jwtOptions = { retry: false, suppressError: true };
      return userService.getUser().pipe(
        switchMap(user => {
          if (!user) {
            userService.logout();
            return this.formatError(err, jwtOptions);
          }

          return this.request(err.config.method as Method, err.config.url, err.config.data || err.config.params);
        })
      );
    }

    if ((err.response?.data as any)?.dataValidation) {
      notification.error({ message: 'Por favor preencha todos os campos corretamente' });
    }

    return this.formatError(err, option);
  }

  private formatError(err: AxiosError, option: RequestOptions) {
    if (option.supressError) {
      return EMPTY;
    }

    if (!err.config) {
      throw err;
    }

    throw new ApiError(err.config, err.response, err);
  }
}

const apiService = new ApiService(API_ENDPOINT);
export default apiService;
